import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search, Users, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader, EmptyState } from "@/components/common";
import PostCard, { LISTING } from "@/components/community/PostCard";
import Composer from "@/components/community/Composer";
import { useApi, errMsg } from "@/lib/api";

function Feed({ kind, groupId, groups, extra = {} }) {
  const api = useApi();
  const [posts, setPosts] = useState(null);
  const [q, setQ] = useState("");
  const key = JSON.stringify(extra);
  useEffect(() => {
    const p = new URLSearchParams({ ...(kind ? { kind } : {}), ...(groupId ? { group_id: groupId } : {}), ...JSON.parse(key), ...(q ? { q } : {}) });
    setPosts(null);
    const t = setTimeout(() => api("get", `/community/posts?${p}`).then(setPosts).catch((e) => { toast.error(errMsg(e)); setPosts([]); }), 300);
    return () => clearTimeout(t);
  }, [api, kind, groupId, key, q]);
  return (
    <div className="space-y-4">
      <Composer kind={kind || "post"} groupId={groupId} groups={groups} listingType={extra.listing_type} onCreated={(p) => setPosts((list) => [p, ...(list || [])])} />
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="community-search" className="pl-9 bg-white" placeholder="Search posts..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {posts === null ? <Loader /> : posts.length === 0 ? <EmptyState testId="community-empty" title="Nothing here yet" text="Be the first to post." /> :
        posts.map((p) => <PostCard key={p.id} post={p} onDeleted={(id) => setPosts(posts.filter((x) => x.id !== id))} />)}
    </div>
  );
}

function useGroups() {
  const api = useApi();
  const [groups, setGroups] = useState(null);
  const load = useCallback(() => api("get", "/community/groups").then(setGroups), [api]);
  useEffect(() => { load(); }, [load]);
  return [groups, load];
}

function GroupsGrid({ groups, reload }) {
  const api = useApi();
  const toggle = async (g) => {
    try { await api(g.joined ? "delete" : "post", `/community/groups/${g.id}/join`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {groups.map((g) => (
        <div key={g.id} className="panel p-5 flex flex-col gap-3" data-testid={`group-card-${g.id}`}>
          <Link to={`/community/groups/${g.id}`} className="font-bold text-lg hover:text-sky-700">{g.name}</Link>
          <p className="text-sm text-slate-600 flex-1">{g.description}</p>
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-slate-500">{g.members_count} members · {g.posts_count} posts</span>
            <Button size="sm" variant={g.joined ? "outline" : "default"} onClick={() => toggle(g)} data-testid={`group-join-${g.id}`}>{g.joined ? "Joined" : "Join"}</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Community() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "feed";
  const [scope, setScope] = useState("all");
  const [ltype, setLtype] = useState("all");
  const [groups, reload] = useGroups();
  const feedExtra = useMemo(() => ({ scope }), [scope]);
  const listingExtra = useMemo(() => (ltype === "all" ? {} : { listing_type: ltype }), [ltype]);
  const myGroups = useMemo(() => (groups || []).filter((g) => g.joined), [groups]);
  if (!groups) return <Loader />;
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 min-w-0">
        <p className="eyebrow">Community</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">The wind crew</h1>
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-6">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="feed" data-testid="community-tab-feed">Feed</TabsTrigger>
            <TabsTrigger value="questions" data-testid="community-tab-questions">Q&A</TabsTrigger>
            <TabsTrigger value="listings" data-testid="community-tab-listings">Crew & housing</TabsTrigger>
            <TabsTrigger value="groups" data-testid="community-tab-groups">Groups</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-6">
          {tab === "feed" && (
            <>
              <div className="flex gap-2 mb-4">
                {[["all", "Everyone"], ["network", "My network"]].map(([v, l]) => (
                  <Button key={v} size="sm" variant={scope === v ? "default" : "outline"} onClick={() => setScope(v)} data-testid={`feed-scope-${v}`}>{l}</Button>
                ))}
              </div>
              <Feed groups={groups} extra={feedExtra} />
            </>
          )}
          {tab === "questions" && <Feed kind="question" groups={groups} />}
          {tab === "listings" && (
            <>
              <div className="flex gap-2 mb-4 flex-wrap">
                {[["all", "All"], ...Object.entries(LISTING).map(([k, v]) => [k, v.label])].map(([v, l]) => (
                  <Button key={v} size="sm" variant={ltype === v ? "default" : "outline"} onClick={() => setLtype(v)} data-testid={`listing-filter-${v}`}>{l}</Button>
                ))}
              </div>
              <Feed kind="listing" groups={groups} extra={listingExtra} />
            </>
          )}
          {tab === "groups" && <GroupsGrid groups={groups} reload={reload} />}
        </div>
      </div>
      <aside className="lg:col-span-4 space-y-4 lg:pt-24">
        <div className="panel p-5">
          <p className="eyebrow mb-3">My groups</p>
          {myGroups.length === 0 ? <p className="text-sm text-slate-500">You haven't joined any group yet.</p> : (
            <ul className="space-y-2">{myGroups.map((g) => (
              <li key={g.id}><Link to={`/community/groups/${g.id}`} className="flex items-center gap-2 text-sm font-medium hover:text-sky-700"><Users size={14} />{g.name}</Link></li>
            ))}</ul>
          )}
        </div>
      </aside>
    </div>
  );
}

export function GroupPage() {
  const { id } = useParams();
  const api = useApi();
  const [g, setG] = useState(null);
  useEffect(() => { api("get", `/community/groups/${id}`).then(setG).catch(() => setG(false)); }, [api, id]);
  if (g === null) return <Loader />;
  if (!g) return <EmptyState testId="group-not-found" title="Group not found" />;
  const toggle = async () => {
    try { await api(g.joined ? "delete" : "post", `/community/groups/${id}/join`); setG({ ...g, joined: !g.joined, members_count: g.members_count + (g.joined ? -1 : 1) }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-3xl">
      <Link to="/community?tab=groups" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="group-back-link"><ArrowLeft size={16} />Groups</Link>
      <div className="mt-6 panel p-6 flex flex-wrap items-center justify-between gap-4 relative overflow-hidden">
        <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-sky-500 to-amber-400" />
        <div><p className="eyebrow">Group</p><h1 className="mt-1 text-3xl font-extrabold" data-testid="group-name">{g.name}</h1><p className="text-slate-600 mt-1">{g.description}</p>
          <p className="font-mono text-xs text-slate-500 mt-2">{g.members_count} members</p></div>
        <Button onClick={toggle} variant={g.joined ? "outline" : "default"} data-testid="group-page-join">{g.joined ? "Leave group" : "Join group"}</Button>
      </div>
      <div className="mt-6"><Feed groupId={id} /></div>
    </div>
  );
}
